import { DateValue } from '@internationalized/date';
import { Spacing } from '@persona-id/design-tokens';
import { RangeValue } from '@react-types/shared';
import { isEqual } from 'lodash/fp';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';
import { MEDIA_WIDTHS, mediaQuery } from 'shared/styles/variables';
import styled from 'styled-components';

import AnalyticsDatePicker from 'dashboard/components/AnalyticsDatePicker';
import { AnalyticsDateRange } from 'dashboard/components/AnalyticsDatePicker/AnalyticsDatePicker';
import AnalyticsQueryContainer from 'dashboard/components/AnalyticsQueryContainer';
import RecencyLabel from 'dashboard/components/AnalyticsQueryContainer/RecencyLabel';
import DisabledProductFeature from 'dashboard/components/DisabledProductFeature';
import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import PersonaErrorBoundary from 'dashboard/components/PersonaErrorBoundary';
import MultiSelect, { useLabelTextMapper } from 'dashboard/components-lib/MultiSelect';
import { Period } from 'dashboard/lib/unixTime';
import { AnalyticsQueryTemplate } from 'dashboard/models/analytics-query-template';
import type { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { DynamicFilters, MetricsGroupBy } from 'dashboard/models/metrics-verification';
import { Template } from 'dashboard/models/template';
import { TransactionType } from 'dashboard/models/transaction-type';
import { Verification } from 'dashboard/models/verification';
import {
  FilterRulesForType,
  initialState,
  VerificationGranularitiesByType,
  VerificationMetricsState,
  VerificationTemplatesGroupedByType,
} from 'dashboard/redux/slices/metrics-verification';
import VerificationFilteredMetrics, {
  FilterDimensions,
  FilteredVerificationType,
} from 'dashboard/views/DashboardVerificationAnalyticsVolumeView/VerificationFilteredMetrics';
import IconVerifications from 'images/icons/verifications.svg';
import { ApiModel, VerificationType } from 'lib/interfaces/api';
import { captureMessage } from 'lib/reporting';
import { Iso8601DateString } from 'lib/types';
import { nameDisplay } from 'lib/utils/template';

export const ORDERED_VERIFICATION_TYPE_NAMES_SHORT: Partial<Record<Verification['type'], string>> =
  {
    [ApiModel.VerificationGovernmentId]: 'Government ID',
    [ApiModel.VerificationSelfie]: 'Selfie',
    [ApiModel.VerificationDatabase]: 'Database',
    [ApiModel.VerificationDatabaseBusiness]: 'Business Registry',
    [ApiModel.VerificationDocument]: 'Document',
    [ApiModel.VerificationPhoneNumber]: 'Phone',
    [ApiModel.VerificationDatabaseTin]: 'TIN',
    [ApiModel.VerificationDatabasePhoneCarrier]: 'Phone Carrier',
    [ApiModel.VerificationEmailAddress]: 'Email',
    [ApiModel.VerificationDatabaseStandard]: 'Database Standard',
    [ApiModel.VerificationAamva]: 'AAMVA',
    [ApiModel.VerificationGovernmentIdNfc]: 'Government ID NFC',
    [ApiModel.VerificationMobileDriversLicense]: 'Mobile Driver’s License',
    [ApiModel.VerificationDatabaseSerpro]: 'Serpro',
    [ApiModel.VerificationDatabaseEcbsv]: 'eCBSV',
  };

export const TYPE_FILTER_OPTIONS: Array<{
  label: string;
  value: Verification['type'];
}> = Object.entries(ORDERED_VERIFICATION_TYPE_NAMES_SHORT).map(([verificationType, label]) => ({
  label,
  value: verificationType as Verification['type'],
}));

const SearchAndFilterContainer = styled.div`
  max-width: 900px;
  flex: auto;
  display: grid;
  align-items: end;
  grid-template-columns: minmax(300px, 3fr) 1fr 1fr minmax(200px, 1fr);
  grid-gap: ${Spacing[4]};

  @media ${mediaQuery({ max: MEDIA_WIDTHS.desktop })} {
    grid-template-columns: none;
    grid-template-rows: auto auto;
  }
`;

const DEFAULT_BAR_KEYS = {
  processed: 'Processed',
};

const DEFAULT_LINE_KEYS = {
  'pass-rate': 'Pass Rate',
};

export interface DashboardVerificationAnalyticsVolumeViewProps
  extends Pick<
    VerificationMetricsState,
    | 'filteredVolumeSummary'
    | 'verificationVolumeOverTime'
    | 'filters'
    | 'lastQueryFilters'
    | 'filterRules'
    | 'volumeLastQueryFilterRules'
    | 'verificationGranularities'
  > {
  handleTemplateFilterUpdate: (templateIds: string[]) => void;
  handleTxnTypesFilterUpdate: (txnTypeIds: string[]) => void;
  handleGroupByFilterToggle?: (value: boolean) => void;
  handleBulkRulesUpdate: (filterRules: Record<VerificationType, FilterRulesForType>) => void;
  handleAddAndRule: (type: VerificationType, orRuleIndex?: number) => void;
  volumeLastGranularities: VerificationGranularitiesByType;
  handleUpdateAndRule: (
    type: FilteredVerificationType,
    orRuleIndex: number,
    andRuleIndex: number,
    dimension?: FilterDimensions,
    operator?: string,
    values?: string[],
  ) => void;
  handleDeleteAndRule: (
    type: FilteredVerificationType,
    orRuleIndex: number,
    andRuleIndex: number,
  ) => void;
  handleAddOrRule: (type: FilteredVerificationType) => void;
  handleGroupByDimensionUpdate: (
    type: FilteredVerificationType,
    dimension: FilterDimensions,
  ) => Promise<void>;
  handleSetFilters: (verificationTypes: string[]) => void;
  handleVerificationTemplateFilterUpdate: (
    type: VerificationType,
    verificationTemplates: string[],
  ) => void;
  loading: boolean;
  error: boolean;
  loadVolumeMetrics: () => Promise<void>;
  queriedAt: Iso8601DateString;
  templates: (Template | InquiryTemplate)[];
  productFeatureIsDisabled: boolean;
  verificationTemplates: VerificationTemplatesGroupedByType;
  txnTypes: TransactionType[];
  dynamicFilterOptions: DynamicFilters;
  handleDateUpdateV2: (range: RangeValue<DateValue> | null) => void;
  handleUpdateGranularity: (type: VerificationType, period: Period) => void;
  resetFiltersToInitialState: () => void;
  openCreateQueryTemplateModal: () => void;
  queryTemplates: AnalyticsQueryTemplate[];
  selectedVolumeQueryTemplate: AnalyticsQueryTemplate | null;
  handleLoadQueryTemplate: (template: AnalyticsQueryTemplate) => void;
  isQueryTemplateModified: boolean;
  unsetSelectedVolumeQueryTemplate: () => void;
}

const DashboardVerificationAnalyticsVolumeView = ({
  error,
  filterRules,
  filteredVolumeSummary,
  filters,
  handleAddAndRule,
  handleAddOrRule,
  handleBulkRulesUpdate,
  handleDateUpdateV2,
  handleDeleteAndRule,
  handleGroupByDimensionUpdate,
  handleGroupByFilterToggle,
  handleSetFilters,
  handleTemplateFilterUpdate,
  handleTxnTypesFilterUpdate,
  handleUpdateGranularity,
  handleUpdateAndRule,
  handleVerificationTemplateFilterUpdate,
  lastQueryFilters,
  loadVolumeMetrics,
  loading,
  productFeatureIsDisabled,
  queriedAt,
  templates,
  verificationTemplates,
  txnTypes,
  dynamicFilterOptions,
  verificationVolumeOverTime,
  volumeLastQueryFilterRules,
  verificationGranularities,
  volumeLastGranularities,
  resetFiltersToInitialState,
  openCreateQueryTemplateModal,
  queryTemplates,
  handleLoadQueryTemplate,
  selectedVolumeQueryTemplate,
  isQueryTemplateModified,
  unsetSelectedVolumeQueryTemplate,
}: DashboardVerificationAnalyticsVolumeViewProps) => {
  const { createdAtStart, createdAtEnd, templateIds, verificationTypes, txnTypeIds, groupBy } =
    filters;
  const dateRange = useMemo<AnalyticsDateRange>(() => {
    const startDate = createdAtStart
      ? DateTime.fromISO(createdAtStart, { setZone: true })
      : undefined;
    const endDate = createdAtEnd ? DateTime.fromISO(createdAtEnd, { setZone: true }) : undefined;
    return {
      startDate,
      endDate,
    };
  }, [createdAtStart, createdAtEnd]);

  const templateFilterOptions = useMemo(
    () =>
      templates.map((template) => ({
        label: nameDisplay(template),
        value: template.id,
      })),
    [templates],
  );

  const templateFilterLabelTextMapper = useLabelTextMapper(templateFilterOptions, 'templates', {
    emptyIsAll: true,
  });

  const txnFilterOptions = useMemo(
    () =>
      txnTypes.map((txn) => ({
        label: txn.attributes.name,
        value: txn.id,
      })),
    [txnTypes],
  );

  const txnsFilterLabelTextMapper = useLabelTextMapper(txnFilterOptions, 'types', {
    emptyIsAll: true,
  });

  const verificationTypeFilterLabelTextMapper = useLabelTextMapper(
    TYPE_FILTER_OPTIONS,
    'verification types',
    {
      emptyIsAll: true,
    },
  );

  // On an initial page click, we want to load metrics
  const loaded = queriedAt != null;

  // Track whether we've opened a new filter panel (thus, we cannot compare the current + previous filters)
  const [filtersOpen, handleOpenFilters] = useState(false);

  useEffect(() => {
    // When alternating between tabs, we'll need to ensure that the current set of filter rules matches the last query of filterRules
    // Furthermore, we're not going to want to auto load a new query when we select a new verification template
    const requiredReload = !isEqual(filterRules, volumeLastQueryFilterRules) && !filtersOpen;
    const requiredPeriodReload = !isEqual(verificationGranularities, volumeLastGranularities);
    // We'll want to let the query parameters kick off another load. This is because we disable the FE features
    // that allow for the user to fire off another query.
    if (
      !productFeatureIsDisabled &&
      (requiredReload || !loaded || requiredPeriodReload) &&
      !error
    ) {
      loadVolumeMetrics();
    }
  }, [loaded, error, filterRules, filtersOpen]);

  const hasData = useMemo(() => {
    return (
      !isEqual(filteredVolumeSummary, initialState.filteredVolumeSummary) &&
      !isEqual(verificationVolumeOverTime, initialState.verificationVolumeOverTime)
    );
  }, [filteredVolumeSummary, verificationVolumeOverTime]);

  if (productFeatureIsDisabled) {
    return (
      <DisabledProductFeature
        title='Verification Analytics'
        prompt='Understand insights about your data'
        icon={<IconVerifications />}
        helpLink={null}
        popupMessage='Your current plan does not have access to this feature.'
      />
    );
  }
  const renderSearchAndFilterWrapper = () => (
    <SearchAndFilterContainer>
      <AnalyticsDatePicker
        dataTest='metrics-verifications-view__date-range'
        handleDatesChangeV2={handleDateUpdateV2}
        dateRange={dateRange}
        disabled={loading}
      />
      <div>
        <FormControlLabel>Verification type</FormControlLabel>
        <MultiSelect
          value={verificationTypes}
          selectOptionValues={TYPE_FILTER_OPTIONS}
          labelTextMapper={verificationTypeFilterLabelTextMapper}
          onChange={handleSetFilters}
          disabled={loading}
          size='md'
          data-test='metrics-verifications-inquiry-templates-modal__select'
        />
      </div>
      {templates.length > 1 && (
        <div>
          <FormControlLabel>Inquiry templates</FormControlLabel>
          <MultiSelect
            value={templateIds}
            selectOptionValues={templateFilterOptions}
            labelTextMapper={templateFilterLabelTextMapper}
            onChange={handleTemplateFilterUpdate}
            disabled={loading}
            size='md'
            data-test='metrics-verifications-view__select-template'
          />
        </div>
      )}

      {txnTypes.length > 1 && (
        <div>
          <FormControlLabel>Transaction type</FormControlLabel>
          <MultiSelect
            value={txnTypeIds}
            selectOptionValues={txnFilterOptions}
            labelTextMapper={txnsFilterLabelTextMapper}
            onChange={handleTxnTypesFilterUpdate}
            disabled={loading}
            size='md'
            data-test='metrics-verifications-view__select-txn-type'
          />
        </div>
      )}
    </SearchAndFilterContainer>
  );

  return (
    <>
      <AnalyticsQueryContainer
        selectedQueryTemplate={selectedVolumeQueryTemplate}
        queryTemplates={queryTemplates}
        loading={loading}
        onLoad={handleLoadQueryTemplate}
        onRun={loadVolumeMetrics}
        onSave={openCreateQueryTemplateModal}
        onClear={resetFiltersToInitialState}
        useGroupByToggle={true}
        groupByLabel='Group by Inquiries'
        groupByOnChange={handleGroupByFilterToggle}
        groupByValue={groupBy === MetricsGroupBy.Inquiry}
        isQueryTemplateModified={isQueryTemplateModified}
        unsetSelectedTemplate={unsetSelectedVolumeQueryTemplate}
        recencyLabel={
          <>
            {filteredVolumeSummary?.data?.length > 0 && (
              <RecencyLabel
                queriedAt={queriedAt}
                recencyIso={filteredVolumeSummary?.data[0]['updated-at']}
              />
            )}
          </>
        }
      >
        {renderSearchAndFilterWrapper()}
      </AnalyticsQueryContainer>
      {hasData && (
        <PersonaErrorBoundary
          onCatch={(error) =>
            captureMessage('Verification Analytics failed to render', 'error', {
              error,
              component: 'DashboardVerificationAnalyticsVolumeView',
            })
          }
        >
          <VerificationFilteredMetrics
            barKeys={DEFAULT_BAR_KEYS}
            filterRules={filterRules}
            filteredVolumeSummary={filteredVolumeSummary}
            handleAddAndRule={handleAddAndRule}
            handleAddOrRule={handleAddOrRule}
            handleBulkRulesUpdate={handleBulkRulesUpdate}
            handleDeleteAndRule={handleDeleteAndRule}
            handleGroupByUpdate={handleGroupByDimensionUpdate}
            handleLoadMetrics={loadVolumeMetrics}
            handleOpenFilters={handleOpenFilters}
            handleUpdateGranularity={handleUpdateGranularity}
            handleUpdateAndRule={handleUpdateAndRule}
            handleVerificationTemplateFilterUpdate={handleVerificationTemplateFilterUpdate}
            lastQueryFilters={lastQueryFilters}
            lineKeys={DEFAULT_LINE_KEYS}
            loading={loading}
            verificationTemplates={verificationTemplates}
            verificationVolumeOverTime={verificationVolumeOverTime}
            volumeLastQueryFilterRules={volumeLastQueryFilterRules}
            dynamicFilterOptions={dynamicFilterOptions}
            verificationGranularities={verificationGranularities}
          />
        </PersonaErrorBoundary>
      )}
    </>
  );
};

export default DashboardVerificationAnalyticsVolumeView;
